import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import practiceService from '../../services/practiceService';
import { toast } from 'react-toastify';
import { deletePatient } from './patientSlice';
import { deleteProvider } from './providerSlice';
import { deleteCaregiver } from './caregiverSlice';
import { paginationFromThunkAction } from '../../utils/paginationUtils';

// ============================================================================
// ASYNC THUNKS
// ============================================================================

// Get all practices
export const fetchPractices = createAsyncThunk(
  'practice/fetchPractices',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await practiceService.getPractices(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practices');
    }
  }
);

// Get practice by ID
export const fetchPractice = createAsyncThunk(
  'practice/fetchPractice',
  async (id, { rejectWithValue }) => {
    try {
      const response = await practiceService.getPractice(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice');
    }
  }
);

// Get lightweight practice by ID (summary for fast dashboards)
export const fetchPracticeSummary = createAsyncThunk(
  'practice/fetchPracticeSummary',
  async (arg, { rejectWithValue }) => {
    try {
      const id = arg && typeof arg === 'object' ? arg.id : arg;
      const auditView = Boolean(arg && typeof arg === 'object' && arg.auditView);
      const response = await practiceService.getPracticeSummary(id, { auditView });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice summary');
    }
  }
);

// Create practice
export const createPractice = createAsyncThunk(
  'practice/createPractice',
  async (practiceData, { rejectWithValue }) => {
    try {
      const response = await practiceService.createPractice(practiceData);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.sqlError ||
        error.message ||
        'Failed to create practice';
      return rejectWithValue(errorMessage);
    }
  }
);

// Update practice
export const updatePractice = createAsyncThunk(
  'practice/updatePractice',
  async ({ id, practiceData }, { rejectWithValue }) => {
    try {
      const response = await practiceService.updatePractice(id, practiceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update practice');
    }
  }
);

// Delete practice
export const deletePractice = createAsyncThunk(
  'practice/deletePractice',
  async (id, { rejectWithValue }) => {
    try {
      await practiceService.deletePractice(id);
      return { id };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete practice');
    }
  }
);

// Get practice dashboard
export const fetchPracticeDashboard = createAsyncThunk(
  'practice/fetchPracticeDashboard',
  async (id, { rejectWithValue }) => {
    try {
      const response = await practiceService.getPracticeDashboard(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice dashboard');
    }
  }
);

// Get practice statistics
export const fetchPracticeStatistics = createAsyncThunk(
  'practice/fetchPracticeStatistics',
  async (id, { rejectWithValue }) => {
    try {
      const response = await practiceService.getPracticeStats(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice statistics');
    }
  }
);

// Get practice patients
export const fetchPracticePatients = createAsyncThunk(
  'practice/fetchPracticePatients',
  async ({ id, params = {} }, { rejectWithValue }) => {
    try {
      const response = await practiceService.getPracticePatients(id, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice patients');
    }
  }
);

// Get practice patients by program
export const fetchPracticePatientsByProgram = createAsyncThunk(
  'practice/fetchPracticePatientsByProgram',
  async ({ id, params = {} }, { rejectWithValue }) => {
    try {
      const response = await practiceService.getPracticePatientsByProgram(id, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice patients');
    }
  }
);

// Get strict filtered practice patients by program tab
export const fetchPracticePatientsFilteredByProgramTab = createAsyncThunk(
  'practice/fetchPracticePatientsFilteredByProgramTab',
  async ({ id, params = {} }, { rejectWithValue }) => {
    try {
      const response = await practiceService.getPracticePatientsFilteredByProgramTab(id, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch filtered practice patients');
    }
  }
);

// Get practice providers (minimal data for list)
export const fetchPracticeProviders = createAsyncThunk(
  'practice/fetchPracticeProviders',
  async (arg, { rejectWithValue }) => {
    const practiceId = arg?.practiceId ?? arg;
    const params = arg?.params ?? { minimal: true }; // Default to minimal for performance
    try {
      const response = await practiceService.getPracticeProviders(practiceId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice providers');
    }
  }
);

// Get practice caregivers (minimal data for list)
export const fetchPracticeCaregivers = createAsyncThunk(
  'practice/fetchPracticeCaregivers',
  async (arg, { rejectWithValue }) => {
    const practiceId = arg?.practiceId ?? arg;
    const params = arg?.params ?? { minimal: true };
    try {
      const response = await practiceService.getPracticeCaregivers(practiceId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice caregivers');
    }
  }
);

// Get practice admins (minimal data for list)
export const fetchPracticeAdmins = createAsyncThunk(
  'practice/fetchPracticeAdmins',
  async (arg, { rejectWithValue }) => {
    const id = typeof arg === 'object' ? arg.id : arg;
    const params = { minimal: true };
    try {
      const response = await practiceService.getPracticeAdmins(id, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch practice admins');
    }
  }
);

// Get system caregivers
export const fetchSystemCaregivers = createAsyncThunk(
  'practice/fetchSystemCaregivers',
  async (practiceId = null, { rejectWithValue }) => {
    try {
      const response = await practiceService.getSystemCaregivers(practiceId, { page: 1, limit: 2500 });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch system caregivers');
    }
  }
);

// Search practices
export const searchPractices = createAsyncThunk(
  'practice/searchPractices',
  async ({ query, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await practiceService.searchPractices(query, filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search practices');
    }
  }
);

// ============================================================================
// SLICE DEFINITION
// ============================================================================

const initialState = {
  // Practice list data
  practices: [],
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
    has_next_page: false,
    has_prev_page: false
  },

  // Current practice data
  currentPractice: null,
  practiceDashboard: null,
  practiceStatistics: null,

  // Practice management data
  practicePatients: [],
  practicePatientsProgram: null,
  practicePatientsPracticeId: null,
  pendingPatientsProgram: null,
  pendingPatientsPracticeId: null,
  filteredPatientsRequestId: null,
  practiceProviders: [],
  practiceCaregivers: [],
  practiceAdmins: [],
  systemCaregivers: [],

  providersByPracticeId: {},
  caregiversByPracticeId: {},
  practiceProvidersFetched: {},
  practiceCaregiversFetched: {},
  listLoading: {
    patients: false,
    patientsByProgram: false,
    providers: false,
    caregivers: false
  },

  // UI state
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,

  // Error handling
  error: null,
  lastAction: null,

  // Search and filters
  searchTerm: '',
  filters: {
    status: '',
    state: '',
    programType: '',
    dateFrom: '',
    dateTo: '',
    per_page: 10 // Can be a number or 'all'
  },
  sortBy: 'status',
  sortOrder: 'asc'
};

const practiceSlice = createSlice({
  name: 'practice',
  initialState,
  reducers: {
    // Clear errors
    clearError: (state) => {
      state.error = null;
    },

    // Set search term
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },

    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Clear filters
    clearFilters: (state) => {
      state.filters = {
        status: '',
        state: '',
        programType: '',
        dateFrom: '',
        dateTo: '',
        per_page: 10
      };
      state.searchTerm = '';
    },

    // Set sorting
    setSorting: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortBy = sortBy;
      state.sortOrder = sortOrder;
    },

    // Optimistic UI: Add provider immediately
    addProviderOptimistic: (state, action) => {
      const { practiceId, provider } = action.payload;
      const key = String(practiceId);
      if (!state.providersByPracticeId[key]) {
        state.providersByPracticeId[key] = [];
      }
      state.providersByPracticeId[key] = [...state.providersByPracticeId[key], provider];
      state.practiceProviders = [...state.practiceProviders, provider];
    },

    // Optimistic UI: Add caregiver immediately
    addCaregiverOptimistic: (state, action) => {
      const { practiceId, caregiver } = action.payload;
      const key = String(practiceId);
      if (!state.caregiversByPracticeId[key]) {
        state.caregiversByPracticeId[key] = [];
      }
      state.caregiversByPracticeId[key] = [...state.caregiversByPracticeId[key], caregiver];
      state.practiceCaregivers = [...state.practiceCaregivers, caregiver];
    },

    // Optimistic UI: Update provider immediately
    updateProviderOptimistic: (state, action) => {
      const { practiceId, providerId, updates } = action.payload;
      const key = String(practiceId);
      const updateFn = (p) => String(p.id) === String(providerId) ? { ...p, ...updates } : p;

      if (state.providersByPracticeId[key]) {
        state.providersByPracticeId[key] = state.providersByPracticeId[key].map(updateFn);
      }
      state.practiceProviders = state.practiceProviders.map(updateFn);
    },

    // Set current practice
    setCurrentPractice: (state, action) => {
      state.currentPractice = action.payload;
    },

    // Clear current practice
    clearCurrentPractice: (state) => {
      state.currentPractice = null;
      state.practiceDashboard = null;
      state.practiceStatistics = null;
    },

    // Reset practice management data
    resetPracticeManagement: (state) => {
      state.practicePatients = [];
      state.practiceProviders = [];
      state.practiceCaregivers = [];
      state.practiceAdmins = [];
    },

    // Update practice in list
    updatePracticeInList: (state, action) => {
      const list = state.practices ?? [];
      const updatedPractice = action.payload;
      const index = list.findIndex(p => p.id === updatedPractice.id);
      if (index !== -1) {
        state.practices = list.map((p, i) => (i === index ? updatedPractice : p));
      }
    },

    // Remove practice from list
    removePracticeFromList: (state, action) => {
      const targetId = action.payload;
      if (targetId != null) {
        state.practices = (state.practices ?? []).filter(
          p => String(p.id) !== String(targetId) && Number(p.id) !== Number(targetId)
        );
        if (state.pagination) {
          state.pagination.total = Math.max(0, (state.pagination.total || 0) - 1);
          const perPage = state.filters?.per_page === 'all' || state.filters?.per_page === 9999
            ? 9999
            : (state.filters?.per_page || 10);
          state.pagination.total_pages = Math.max(1, Math.ceil(state.pagination.total / perPage));
        }
      }
    },

    invalidatePracticeProvidersCache: (state, action) => {
      const key = String(action.payload);
      delete state.practiceProvidersFetched[key];
      delete state.providersByPracticeId[key];
    },
    invalidatePracticeCaregiversCache: (state, action) => {
      const key = String(action.payload);
      delete state.practiceCaregiversFetched[key];
      delete state.caregiversByPracticeId[key];
    }
  },
  extraReducers: (builder) => {
    builder
      // ========================================================================
      // FETCH PRACTICES
      // ========================================================================
      .addCase(fetchPractices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'fetchPractices';
      })
      .addCase(fetchPractices.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload?.data;
        state.practices = data?.practices ?? state.practices ?? [];
        state.pagination = paginationFromThunkAction(action, data?.pagination ?? state.pagination);
        state.error = null;
      })
      .addCase(fetchPractices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE
      // ========================================================================
      .addCase(fetchPractice.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'fetchPractice';
      })
      .addCase(fetchPractice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPractice = action.payload.data;
        state.error = null;
      })
      .addCase(fetchPractice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE SUMMARY
      // ========================================================================
      .addCase(fetchPracticeSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'fetchPracticeSummary';
      })
      .addCase(fetchPracticeSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPractice = action.payload.data;
        state.error = null;
      })
      .addCase(fetchPracticeSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // CREATE PRACTICE
      // ========================================================================
      .addCase(createPractice.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.lastAction = 'createPractice';
      })
      .addCase(createPractice.fulfilled, (state, action) => {
        state.isCreating = false;
        const newPractice = action.payload?.data;
        if (newPractice) {
          const list = state.practices ?? [];
          state.practices = [newPractice, ...list];
        }
        state.error = null;
      })
      .addCase(createPractice.rejected, (state, action) => {
        state.isCreating = false;
        // Store just the message string, not the entire error object
        state.error = typeof action.payload === 'string'
          ? action.payload
          : action.payload?.message || 'Failed to create practice';
      })

      // ========================================================================
      // UPDATE PRACTICE
      // ========================================================================
      .addCase(updatePractice.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.lastAction = 'updatePractice';
      })
      .addCase(updatePractice.fulfilled, (state, action) => {
        state.isUpdating = false;
        const payloadData = action.payload?.data;
        if (payloadData) {
          const list = state.practices ?? [];
          const index = list.findIndex(p => p.id === payloadData.id);
          if (index !== -1) {
            state.practices = list.map((p, i) => (i === index ? { ...p, ...payloadData } : p));
          }
          if (state.currentPractice && state.currentPractice.id === payloadData.id) {
            state.currentPractice = { ...state.currentPractice, ...payloadData };
          }
        }
        state.error = null;
      })
      .addCase(updatePractice.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // ========================================================================
      // DELETE PRACTICE
      // ========================================================================
      .addCase(deletePractice.pending, (state, action) => {
        state.isDeleting = true;
        state.error = null;
        state.lastAction = 'deletePractice';
        const id = action.meta.arg;
        if (id != null) {
          const before = (state.practices ?? []).length;
          state.practices = (state.practices ?? []).filter(
            (p) => String(p.id) !== String(id) && Number(p.id) !== Number(id)
          );
          const removed = before - state.practices.length;
          if (removed > 0 && state.pagination) {
            state.pagination.total = Math.max(0, (state.pagination.total || 0) - removed);
            const perPage = state.filters?.per_page === 'all' || state.filters?.per_page === 9999
              ? 9999
              : (state.filters?.per_page || 10);
            state.pagination.total_pages = Math.max(1, Math.ceil(state.pagination.total / perPage));
          }
        }
      })
      .addCase(deletePractice.fulfilled, (state, action) => {
        state.isDeleting = false;
        const id = action.payload?.id ?? action.meta?.arg;
        if (id != null) {
          state.practices = (state.practices ?? []).filter(
            (p) => String(p.id) !== String(id) && Number(p.id) !== Number(id)
          );
        }
        state.error = null;
      })
      .addCase(deletePractice.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE DASHBOARD
      // ========================================================================
      .addCase(fetchPracticeDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'fetchPracticeDashboard';
      })
      .addCase(fetchPracticeDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.practiceDashboard = action.payload.data;
        state.error = null;
      })
      .addCase(fetchPracticeDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE STATISTICS
      // ========================================================================
      .addCase(fetchPracticeStatistics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'fetchPracticeStatistics';
      })
      .addCase(fetchPracticeStatistics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.practiceStatistics = action.payload.data;
        state.error = null;
      })
      .addCase(fetchPracticeStatistics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE PATIENTS
      // ========================================================================
      .addCase(fetchPracticePatients.pending, (state) => {
        state.listLoading.patients = true;
        state.error = null;
        state.lastAction = 'fetchPracticePatients';
      })
      .addCase(fetchPracticePatients.fulfilled, (state, action) => {
        state.listLoading.patients = false;
        const responseData = action.payload?.data || action.payload;
        state.practicePatients = responseData?.patients || [];
        state.pagination = paginationFromThunkAction(action, responseData?.pagination);
        state.error = null;
      })
      .addCase(fetchPracticePatients.rejected, (state, action) => {
        state.listLoading.patients = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE PATIENTS BY PROGRAM
      // ========================================================================
      .addCase(fetchPracticePatientsByProgram.pending, (state) => {
        state.listLoading.patientsByProgram = true;
        state.error = null;
        state.lastAction = 'fetchPracticePatientsByProgram';
      })
      .addCase(fetchPracticePatientsByProgram.fulfilled, (state, action) => {
        state.listLoading.patientsByProgram = false;
        const responseData = action.payload?.data || action.payload;
        state.practicePatients = responseData?.patients || [];
        state.pagination = paginationFromThunkAction(action, responseData?.pagination);
        state.error = null;
      })
      .addCase(fetchPracticePatientsByProgram.rejected, (state, action) => {
        state.listLoading.patientsByProgram = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH STRICT FILTERED PRACTICE PATIENTS BY PROGRAM TAB
      // ========================================================================
      .addCase(fetchPracticePatientsFilteredByProgramTab.pending, (state, action) => {
        const requestedPracticeId = String(action.meta.arg?.id ?? '');
        const requestedProgram = String(action.meta.arg?.params?.program || 'rpm').toLowerCase();
        state.filteredPatientsRequestId = action.meta.requestId;
        state.listLoading.patientsByProgram = true;
        state.pendingPatientsProgram = requestedProgram;
        state.pendingPatientsPracticeId = requestedPracticeId;
        if (
          requestedProgram !== String(state.practicePatientsProgram || '').toLowerCase() ||
          requestedPracticeId !== String(state.practicePatientsPracticeId ?? '')
        ) {
          state.practicePatients = [];
          state.practicePatientsProgram = null;
          state.practicePatientsPracticeId = null;
        }
        state.error = null;
        state.lastAction = 'fetchPracticePatientsFilteredByProgramTab';
      })
      .addCase(fetchPracticePatientsFilteredByProgramTab.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.filteredPatientsRequestId) return;

        const requestedPracticeId = String(action.meta.arg?.id ?? '');
        const requestedProgram = String(action.meta.arg?.params?.program || 'rpm').toLowerCase();

        state.listLoading.patientsByProgram = false;
        state.pendingPatientsProgram = null;
        state.pendingPatientsPracticeId = null;
        const responseData = action.payload?.data || action.payload;
        state.practicePatients = responseData?.patients || [];
        state.practicePatientsProgram = requestedProgram;
        state.practicePatientsPracticeId = requestedPracticeId;
        state.pagination = paginationFromThunkAction(action, responseData?.pagination);
        state.error = null;
      })
      .addCase(fetchPracticePatientsFilteredByProgramTab.rejected, (state, action) => {
        if (action.meta.requestId !== state.filteredPatientsRequestId) return;
        state.listLoading.patientsByProgram = false;
        state.pendingPatientsProgram = null;
        state.pendingPatientsPracticeId = null;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE PROVIDERS
      // ========================================================================
      .addCase(fetchPracticeProviders.pending, (state, action) => {
        const arg = action.meta.arg;
        const pid = arg?.practiceId ?? arg;
        const key = String(pid);
        const force = typeof arg === 'object' && arg && arg.force;
        if (force) {
          delete state.practiceProvidersFetched[key];
          delete state.providersByPracticeId[key];
        } else if (state.practiceProvidersFetched[key]) {
          return;
        }
        state.listLoading.providers = true;
        state.error = null;
        state.lastAction = 'fetchPracticeProviders';
      })
      .addCase(fetchPracticeProviders.fulfilled, (state, action) => {
        state.listLoading.providers = false;
        const arg = action.meta.arg;
        const pid = arg?.practiceId ?? arg;
        const key = String(pid);
        const raw = action.payload.data;
        const list = Array.isArray(raw) ? raw : (raw || []);
        state.practiceProviders = list;
        state.providersByPracticeId[key] = list;
        state.practiceProvidersFetched[key] = true;
        state.error = null;
      })
      .addCase(fetchPracticeProviders.rejected, (state, action) => {
        state.listLoading.providers = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE CAREGIVERS
      // ========================================================================
      .addCase(fetchPracticeCaregivers.pending, (state, action) => {
        const arg = action.meta.arg;
        const pid = arg?.practiceId ?? arg;
        const key = String(pid);
        const force = typeof arg === 'object' && arg && arg.force;
        if (force) {
          delete state.practiceCaregiversFetched[key];
          delete state.caregiversByPracticeId[key];
        } else if (state.practiceCaregiversFetched[key]) {
          return;
        }
        state.listLoading.caregivers = true;
        state.error = null;
        state.lastAction = 'fetchPracticeCaregivers';
      })
      .addCase(fetchPracticeCaregivers.fulfilled, (state, action) => {
        state.listLoading.caregivers = false;
        const arg = action.meta.arg;
        const pid = arg?.practiceId ?? arg;
        const key = String(pid);
        const raw = action.payload.data;
        const list = Array.isArray(raw) ? raw : (raw || []);
        state.practiceCaregivers = list;
        state.caregiversByPracticeId[key] = list;
        state.practiceCaregiversFetched[key] = true;
        state.error = null;
      })
      .addCase(fetchPracticeCaregivers.rejected, (state, action) => {
        state.listLoading.caregivers = false;
        state.error = action.payload;
      })

      // ========================================================================
      // FETCH PRACTICE ADMINS
      // ========================================================================
      .addCase(fetchPracticeAdmins.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'fetchPracticeAdmins';
      })
      .addCase(fetchPracticeAdmins.fulfilled, (state, action) => {
        state.isLoading = false;
        state.practiceAdmins = action.payload.data;
        state.error = null;
      })
      .addCase(fetchPracticeAdmins.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // System Caregivers
      .addCase(fetchSystemCaregivers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'fetchSystemCaregivers';
      })
      .addCase(fetchSystemCaregivers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.systemCaregivers = action.payload.data;
        state.error = null;
      })
      .addCase(fetchSystemCaregivers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // SEARCH PRACTICES
      // ========================================================================
      .addCase(searchPractices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastAction = 'searchPractices';
      })
      .addCase(searchPractices.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload?.data;
        state.practices = data?.practices ?? state.practices ?? [];
        state.pagination = data?.pagination ?? state.pagination;
      })
      .addCase(searchPractices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // OPTIMISTIC UI: Handle deletions from other slices
      // ========================================================================
      .addCase(deletePatient.pending, (state, action) => {
        const pid = action.meta.arg.patientId;
        state.practicePatients = state.practicePatients.filter(p => {
          const id = p.patient_table_id ?? p.id;
          return String(id) !== String(pid);
        });
      })
      .addCase(deleteProvider.pending, (state, action) => {
        const { practiceId, providerId } = action.meta.arg;
        const key = String(practiceId);
        if (state.providersByPracticeId[key]) {
          state.providersByPracticeId[key] = state.providersByPracticeId[key].filter(
            p => String(p.id) !== String(providerId)
          );
        }
        state.practiceProviders = state.practiceProviders.filter(
          p => String(p.id) !== String(providerId)
        );
      })
      // Rollback or finalize provider creation
      .addCase('provider/createProvider/fulfilled', (state, action) => {
        const { practiceId, providerData = {} } = action.meta.arg;
        const key = String(practiceId);
        const tempId = action.meta.arg.tempId;
        const realProvider = action.payload.data || action.payload || {};
        const hydratedProvider = {
          ...realProvider,
          name: realProvider.name ?? [realProvider.first_name ?? providerData.first_name ?? '', realProvider.last_name ?? providerData.last_name ?? ''].filter(Boolean).join(' ').trim(),
          first_name: realProvider.first_name ?? providerData.first_name ?? '',
          last_name: realProvider.last_name ?? providerData.last_name ?? '',
          email: realProvider.email ?? providerData.email ?? '',
          status: realProvider.status ?? providerData.status ?? 'Active',
          phone: realProvider.phone ?? providerData.phone ?? '',
          home_phone: realProvider.home_phone ?? providerData.phone ?? '',
          cell_phone: realProvider.cell_phone ?? providerData.cell_phone ?? '',
          fax: realProvider.fax ?? providerData.fax ?? '',
          address_line_1: realProvider.address_line_1 ?? providerData.address_line_1 ?? '',
          address_line_2: realProvider.address_line_2 ?? providerData.address_line_2 ?? '',
          city: realProvider.city ?? providerData.city ?? '',
          zip_code: realProvider.zip_code ?? providerData.zip_code ?? '',
          address: {
            ...(realProvider.address || {}),
            address_line_1: realProvider.address?.address_line_1 ?? realProvider.address_line_1 ?? providerData.address_line_1 ?? '',
            address_line_2: realProvider.address?.address_line_2 ?? realProvider.address_line_2 ?? providerData.address_line_2 ?? '',
            city: realProvider.address?.city ?? realProvider.city ?? providerData.city ?? '',
            zip_code: realProvider.address?.zip_code ?? realProvider.zip_code ?? providerData.zip_code ?? ''
          }
        };

        if (tempId) {
          const replaceFn = (p) => String(p.id) === String(tempId) ? hydratedProvider : p;
          if (state.providersByPracticeId[key]) {
            state.providersByPracticeId[key] = state.providersByPracticeId[key].map(replaceFn);
          }
          state.practiceProviders = state.practiceProviders.map(replaceFn);
        }
      })
      .addCase('provider/createProvider/rejected', (state, action) => {
        const { practiceId, tempId } = action.meta.arg;
        if (tempId) {
          const key = String(practiceId);
          const filterFn = (p) => String(p.id) !== String(tempId);
          if (state.providersByPracticeId[key]) {
            state.providersByPracticeId[key] = state.providersByPracticeId[key].filter(filterFn);
          }
          state.practiceProviders = state.practiceProviders.filter(filterFn);
        }
      })
      .addCase('caregiver/createCaregiver/fulfilled', (state, action) => {
        const { practiceId, tempId, caregiverData = {} } = action.meta.arg;
        const key = String(practiceId);
        const realCaregiver = action.payload.data || action.payload || {};
        const twoWayFromApi =
          realCaregiver.is_two_way_auth ??
          realCaregiver.two_way_authentication_enabled ??
          realCaregiver.two_way_auth;
        const twoWayFromRequest = caregiverData.is_two_way_auth;
        const resolvedTwoWay =
          twoWayFromApi !== undefined && twoWayFromApi !== null
            ? twoWayFromApi
            : twoWayFromRequest;

        const hydratedCaregiver = {
          ...realCaregiver,
          name: realCaregiver.name ?? [realCaregiver.first_name ?? caregiverData.first_name ?? '', realCaregiver.last_name ?? caregiverData.last_name ?? ''].filter(Boolean).join(' ').trim(),
          first_name: realCaregiver.first_name ?? caregiverData.first_name ?? '',
          last_name: realCaregiver.last_name ?? caregiverData.last_name ?? '',
          email: realCaregiver.email ?? caregiverData.email ?? '',
          status: realCaregiver.status ?? caregiverData.status ?? 'Active',
          phone: realCaregiver.phone ?? caregiverData.phone ?? '',
          home_phone: realCaregiver.home_phone ?? caregiverData.phone ?? '',
          cell_phone: realCaregiver.cell_phone ?? caregiverData.cell_phone ?? '',
          fax: realCaregiver.fax ?? caregiverData.fax ?? '',
          address_line_1: realCaregiver.address_line_1 ?? caregiverData.address_line_1 ?? '',
          address_line_2: realCaregiver.address_line_2 ?? caregiverData.address_line_2 ?? '',
          city: realCaregiver.city ?? caregiverData.city ?? '',
          zip_code: realCaregiver.zip_code ?? caregiverData.zip_code ?? '',
          // Create API often omits this; detail UI reads it from list cache — merge from request payload.
          is_two_way_auth: resolvedTwoWay,
          two_way_authentication_enabled:
            realCaregiver.two_way_authentication_enabled ??
            (resolvedTwoWay === 1 || resolvedTwoWay === true),
          address: {
            ...(realCaregiver.address || {}),
            address_line_1: realCaregiver.address?.address_line_1 ?? realCaregiver.address_line_1 ?? caregiverData.address_line_1 ?? '',
            address_line_2: realCaregiver.address?.address_line_2 ?? realCaregiver.address_line_2 ?? caregiverData.address_line_2 ?? '',
            city: realCaregiver.address?.city ?? realCaregiver.city ?? caregiverData.city ?? '',
            zip_code: realCaregiver.address?.zip_code ?? realCaregiver.zip_code ?? caregiverData.zip_code ?? ''
          }
        };

        if (tempId) {
          const replaceFn = (c) => String(c.id) === String(tempId) ? hydratedCaregiver : c;
          if (state.caregiversByPracticeId[key]) {
            state.caregiversByPracticeId[key] = state.caregiversByPracticeId[key].map(replaceFn);
          }
          state.practiceCaregivers = state.practiceCaregivers.map(replaceFn);
        }
      })
      .addCase('caregiver/createCaregiver/rejected', (state, action) => {
        const { practiceId, tempId } = action.meta.arg;
        if (tempId) {
          const key = String(practiceId);
          const filterFn = (c) => String(c.id) !== String(tempId);
          if (state.caregiversByPracticeId[key]) {
            state.caregiversByPracticeId[key] = state.caregiversByPracticeId[key].filter(filterFn);
          }
          state.practiceCaregivers = state.practiceCaregivers.filter(filterFn);
        }
      })
      .addCase('provider/updateProvider/fulfilled', (state, action) => {
        const { practiceId, providerId } = action.meta.arg;
        const key = String(practiceId);
        const updatedProvider = action.payload?.data || action.payload;
        const mergeProvider = (p) =>
          String(p.id) === String(providerId) ? { ...p, ...updatedProvider } : p;

        if (state.providersByPracticeId[key]) {
          state.providersByPracticeId[key] = state.providersByPracticeId[key].map(mergeProvider);
        }
        state.practiceProviders = state.practiceProviders.map(mergeProvider);
      })
      .addCase('caregiver/updateCaregiver/fulfilled', (state, action) => {
        const { practiceId, caregiverId } = action.meta.arg;
        const key = String(practiceId);
        const updatedCaregiver = action.payload?.data || action.payload;
        const mergeCaregiver = (c) =>
          String(c.id) === String(caregiverId) ? { ...c, ...updatedCaregiver } : c;

        if (state.caregiversByPracticeId[key]) {
          state.caregiversByPracticeId[key] = state.caregiversByPracticeId[key].map(mergeCaregiver);
        }
        state.practiceCaregivers = state.practiceCaregivers.map(mergeCaregiver);
      })
      .addCase(deleteCaregiver.pending, (state, action) => {
        const { practiceId, caregiverId } = action.meta.arg;
        const key = String(practiceId);
        if (state.caregiversByPracticeId[key]) {
          state.caregiversByPracticeId[key] = state.caregiversByPracticeId[key].filter(
            c => String(c.id) !== String(caregiverId)
          );
        }
        state.practiceCaregivers = state.practiceCaregivers.filter(
          c => String(c.id) !== String(caregiverId)
        );
      });
  },
});

// Export actions
export const {
  clearError,
  setSearchTerm,
  setFilters,
  clearFilters,
  setSorting,
  setCurrentPractice,
  clearCurrentPractice,
  resetPracticeManagement,
  updatePracticeInList,
  removePracticeFromList,
  invalidatePracticeProvidersCache,
  invalidatePracticeCaregiversCache,
  addProviderOptimistic,
  addCaregiverOptimistic,
  updateProviderOptimistic
} = practiceSlice.actions;

// Export reducer
export default practiceSlice.reducer;